import { useQuery } from '@apollo/client';

import { constructLanguageMap } from 'bundles/common/utils/subtitleUtils';
import type TrackList from 'bundles/interactive-transcript/models/TrackList';
import { buildTracks } from 'bundles/interactive-transcript/utils/TrackUtils';
import type { OnDemandLectureVideosV1GetQueryVariables } from 'bundles/naptimejs/resources/__generated__/OnDemandLectureVideosV1';
import type { OnDemandVideosV1 } from 'bundles/naptimejs/resources/__generated__/OnDemandVideosV1';
import LectureVideosQuery from 'bundles/unified-seo-content-common/queries/LectureVideosQuery';
import type {
  VLPCourseDetailsQuery_XdpV1Resource_slug_elements_xdpMetadata_XdpV1_cdpMetadataMember_cdpMetadata as CdpMetadataType,
  VLPCourseDetailsQuery as CourseDetailsQueryData,
  VLPCourseDetailsQueryVariables as CourseDetailsQueryVariables,
} from 'bundles/unified-seo-content-common/queries/__generated__/VLPCourseDetailsQuery';
import type {
  VLPFetchProductOverviewQuery as FetchProductOverviewQueryData,
  VLPFetchProductOverviewQueryVariables as FetchProductOverviewQueryVariables,
} from 'bundles/unified-seo-content-common/queries/__generated__/VLPFetchProductOverviewQuery';
import {
  CourseDetailsQuery,
  FetchProductOverviewQuery,
} from 'bundles/unified-seo-content-common/queries/assemblerQueries';
import { getDeferToCSRQueryOptions } from 'bundles/unified-seo-content-common/utils';
import type { ReviewOverview } from 'bundles/video-logged-out-page/types/vlpSharedTypes';

type CourseDetailsQueryProps = {
  courseId?: string;
  cdpMetadata?: CdpMetadataType | null;
};

type FetchProductOverviewQueryProps = {
  reviewOverview?: ReviewOverview | null;
};

type LectureVideoQueryData = {
  video?: OnDemandLectureVideosV1 | null;
  onDemandVideos?: OnDemandVideosV1 | null;
};

type TracksData = {
  tracks?: TrackList;
};

interface VideoId {
  id: string;
}
interface OnDemandLectureVideosV1 {
  id: string;
  courseId: string;
  itemId: string;
  videoId: VideoId;
}

interface OnDemandLectureVideosV1Connection {
  elements: Array<OnDemandLectureVideosV1> | null;
  linked?: {
    onDemandVideosV1: Array<OnDemandVideosV1> | null;
  };
}
interface OnDemandLectureVideosV1GetQuery {
  OnDemandLectureVideosV1: {
    get: OnDemandLectureVideosV1Connection | null;
  } | null;
}

export type VLPVideoMetadataProps = CourseDetailsQueryProps &
  LectureVideoQueryData &
  FetchProductOverviewQueryProps &
  TracksData & { loading: boolean };

const useVLPVideoMetadata = ({ courseSlug, itemId }: { courseSlug: string; itemId: string }): VLPVideoMetadataProps => {
  const {
    data: courseDetailsQueryData,
    loading: courseDetailsQueryLoading,
    error: courseDetailsQueryError,
  } = useQuery<CourseDetailsQueryData, CourseDetailsQueryVariables>(CourseDetailsQuery, {
    skip: !courseSlug,
    variables: { courseSlug },
    ...getDeferToCSRQueryOptions(),
  });

  const {
    data: productOverviewQueryData,
    loading: productOverviewQueryLoading,
    error: productOverviewQueryError,
  } = useQuery<FetchProductOverviewQueryData, FetchProductOverviewQueryVariables>(FetchProductOverviewQuery, {
    skip: !courseSlug,
    variables: { courseSlug },
    ...getDeferToCSRQueryOptions(),
  });

  const xdpMetadata = courseDetailsQueryData?.XdpV1Resource?.slug?.elements?.[0]?.xdpMetadata;
  const cdpMetadata = xdpMetadata?.__typename === 'XdpV1_cdpMetadataMember' ? xdpMetadata?.cdpMetadata : null;
  const courseId = cdpMetadata?.id;

  const {
    data: lectureVideoQueryData,
    loading: lectureVideoQueryLoading,
    error: lectureVideoQueryError,
  } = useQuery<OnDemandLectureVideosV1GetQuery, OnDemandLectureVideosV1GetQueryVariables>(LectureVideosQuery, {
    skip: !courseId || !itemId,
    variables: { id: courseId + '~' + itemId },
    ...getDeferToCSRQueryOptions(),
  });

  if (courseDetailsQueryError || productOverviewQueryError || lectureVideoQueryError) {
    return {
      loading: courseDetailsQueryLoading || productOverviewQueryLoading || lectureVideoQueryLoading,
    };
  }

  const lectureVideos = lectureVideoQueryData?.OnDemandLectureVideosV1?.get;
  const video = lectureVideos?.elements?.[0];
  const onDemandVideos = lectureVideos?.linked?.onDemandVideosV1?.[0];

  const vLPVideoMetadataProps = {
    courseId,
    cdpMetadata,
    reviewOverview: productOverviewQueryData?.XdpV1Resource?.slug?.elements?.[0]?.fetchProductOverview?.elements?.[0],
    video,
    onDemandVideos,
    loading: courseDetailsQueryLoading || productOverviewQueryLoading || lectureVideoQueryLoading,
  };

  if (!onDemandVideos) {
    return vLPVideoMetadataProps;
  }

  const languageMap = constructLanguageMap(onDemandVideos.subtitlesVtt, onDemandVideos.subtitlesAssetTags);
  const tracks = buildTracks(languageMap);

  return {
    ...vLPVideoMetadataProps,
    tracks,
  };
};

export default useVLPVideoMetadata;
